/**
 * Global cross-sell SSE channel (per-tenant), mounted once in AppLayout.
 *
 * Keeps a single EventSource alive (`/api/v1/cross-sell/events/stream`, cookie
 * auth) while the user is logged in AND has cross-sell access. Drives the
 * `useCrossSellQueueStore` so jobs started in any tab surface in the header
 * badge + drawer + toast.
 *
 * ERRATA §E9 (BINDING):
 *  - first SSE event is named `snapshot`; subsequent are `status` / domain
 *    event types — consume via `addEventListener`, NEVER `onmessage` (named
 *    events don't fire `onmessage`).
 *  - first-event fallback timeout is 5s.
 *
 * On `cross_sell.whitespace.recomputed` we invalidate the `whitespace` query
 * cache so any open whitespace tab refetches (EDA §4.4).
 *
 * Reconnect strategy: when `onerror` fires with readyState=CLOSED the browser
 * will NOT auto-reconnect (happens on QUIC write errors, 4xx/5xx responses, or
 * fatal transport failures). We schedule a manual reconnect that calls
 * `crossSellApi.globalStreamUrl()` fresh — so the current JWT is used rather
 * than the stale token baked into the original URL. After MAX_CONSECUTIVE_ERRORS
 * failures without a successful first event we downgrade to polling mode and
 * stop retrying.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCrossSellQueueStore } from '../stores/useCrossSellQueueStore';
import { crossSellApi } from '../services/crossSellApi';
import type { CrossSellEventEnvelope } from '../types';

const FIRST_EVENT_TIMEOUT_MS = 5_000;
const MAX_CONSECUTIVE_ERRORS = 5;
const RECONNECT_DELAY_MS = 3_000;

type NamedHandler = (e: MessageEvent) => void;

function parseEnvelope(raw: string): CrossSellEventEnvelope | null {
  try {
    return JSON.parse(raw) as CrossSellEventEnvelope;
  } catch {
    return null;
  }
}

export function useCrossSellGlobalStream(enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    const store = useCrossSellQueueStore.getState;

    let source: EventSource | null = null;
    let consecutiveErrors = 0;
    let firstEventTimer: ReturnType<typeof setTimeout> | undefined;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
    let destroyed = false;

    // Stable handler references — declared once, attached/detached on every
    // reconnect cycle, so removeEventListener always finds the right function.

    const clearFirstTimer = () => {
      if (firstEventTimer) { clearTimeout(firstEventTimer); firstEventTimer = undefined; }
      consecutiveErrors = 0;
      store().noteEvent();
      if (store().streamMode !== 'sse') store().setStreamMode('sse');
    };

    const onSnapshot: NamedHandler = () => clearFirstTimer();
    const onPing: NamedHandler = () => store().noteEvent();

    const onSubmitted: NamedHandler = (e) => {
      clearFirstTimer();
      const evt = parseEnvelope(e.data);
      if (!evt) return;
      const p = evt.payload ?? {};
      const jobId = String(p.job_id ?? p.jobId ?? '');
      if (!jobId) return;
      store().enqueue({
        jobId,
        type: 'bulk_import',
        label: String(p.file_name_original ?? p.fileNameOriginal ?? jobId),
        status: 'processing',
      });
    };

    const onProgress: NamedHandler = (e) => {
      clearFirstTimer();
      const evt = parseEnvelope(e.data);
      if (!evt) return;
      const p = evt.payload ?? {};
      const jobId = String(p.job_id ?? p.jobId ?? '');
      if (!jobId) return;
      store().markStatus(jobId, 'processing', {
        progress: typeof p.progress === 'number' ? (p.progress as number) : undefined,
        etaSeconds:
          typeof p.eta_seconds === 'number'
            ? (p.eta_seconds as number)
            : typeof p.etaSeconds === 'number'
              ? (p.etaSeconds as number)
              : undefined,
      });
    };

    const onProcessed: NamedHandler = (e) => {
      clearFirstTimer();
      const evt = parseEnvelope(e.data);
      if (!evt) return;
      const p = evt.payload ?? {};
      const jobId = String(p.job_id ?? p.jobId ?? '');
      if (!jobId) return;
      const terminal = String(p.terminal_status ?? p.status ?? 'completed');
      const status = terminal === 'failed' || terminal === 'cancelled' ? terminal : 'completed';
      store().markStatus(jobId, status as 'completed' | 'failed' | 'cancelled', {
        accountsImported: Number(p.accounts_imported ?? p.accountsImported ?? 0),
        assetsImported: Number(p.assets_imported ?? p.assetsImported ?? 0),
        decisoresImported: Number(p.decisores_imported ?? p.decisoresImported ?? 0),
      });
    };

    const onWhitespaceRecomputed: NamedHandler = () => {
      clearFirstTimer();
      void queryClient.invalidateQueries({ queryKey: ['cross-sell-whitespace'] });
    };

    function attach(es: EventSource) {
      es.addEventListener('snapshot', onSnapshot);
      es.addEventListener('status', onSnapshot);
      es.addEventListener('ping', onPing);
      es.addEventListener('cross_sell.bulk_import.submitted', onSubmitted);
      es.addEventListener('cross_sell.bulk_import.progress', onProgress);
      es.addEventListener('cross_sell.bulk_import.processed', onProcessed);
      es.addEventListener('cross_sell.whitespace.recomputed', onWhitespaceRecomputed);
    }

    function detach(es: EventSource) {
      es.onerror = null;
      es.removeEventListener('snapshot', onSnapshot);
      es.removeEventListener('status', onSnapshot);
      es.removeEventListener('ping', onPing);
      es.removeEventListener('cross_sell.bulk_import.submitted', onSubmitted);
      es.removeEventListener('cross_sell.bulk_import.progress', onProgress);
      es.removeEventListener('cross_sell.bulk_import.processed', onProcessed);
      es.removeEventListener('cross_sell.whitespace.recomputed', onWhitespaceRecomputed);
      es.close();
    }

    function connect() {
      if (destroyed) return;
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = undefined; }
      if (firstEventTimer) { clearTimeout(firstEventTimer); firstEventTimer = undefined; }
      if (source) { detach(source); source = null; }

      // Use globalStreamUrl() at connect time so the current JWT is embedded
      // (not the stale token from initial mount).
      source = new EventSource(crossSellApi.globalStreamUrl(), { withCredentials: true });
      attach(source);

      // If nothing arrives within 5s the proxy probably buffered the stream —
      // downgrade to polling so the drawer surfaces the offline warning card.
      firstEventTimer = setTimeout(() => {
        if (destroyed) return;
        if (source) { detach(source); source = null; }
        store().setStreamMode('polling');
      }, FIRST_EVENT_TIMEOUT_MS);

      // When readyState=CONNECTING the browser is already going to retry (server
      // sent `retry:` directive) — nothing to do. When readyState=CLOSED the
      // browser will NOT reconnect; we take over and reconnect manually with a
      // fresh token. After too many consecutive failures we switch to polling.
      source.onerror = () => {
        if (destroyed) return;
        if (source?.readyState !== EventSource.CLOSED) return;
        consecutiveErrors++;
        if (firstEventTimer) { clearTimeout(firstEventTimer); firstEventTimer = undefined; }
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          if (source) { detach(source); source = null; }
          store().setStreamMode('polling');
          return;
        }
        if (source) { detach(source); source = null; }
        reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
      };
    }

    connect();

    return () => {
      destroyed = true;
      if (firstEventTimer) clearTimeout(firstEventTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (source) { detach(source); source = null; }
    };
  }, [enabled, queryClient]);
}
